/**
 * "Drive the rig" gate — Google sign-in + visitor lock queue.
 *
 * Flow:
 *   1. Visitor clicks the hero "drive the rig" button.
 *   2. Gate surface reveals with a Google Sign-In button (GIS popup).
 *      Manual email/name fallback is shown alongside.
 *   3. On sign-in success (GIS callback) OR manual submit, POST to
 *      /api/lp/join-queue with { name, email, picture, credential }.
 *      Runtime appends the record to interest.jsonl and acquires the
 *      visitor lock with email as label.
 *   4. Visitor sees their queue position update from /api/status
 *      polling, and a "currently driving: jane@…" line so they know
 *      who's ahead.
 *   5. When the visitor becomes the lock holder, the gate flips to
 *      the "you're driving" countdown. The existing live-wire.ts
 *      hookup makes the demo controls (already on the page) live
 *      against /api/uniform etc. for the duration of the slot.
 *   6. When seconds_remaining hits zero, the gate auto-flips back to
 *      the queued state for the next person.
 *
 * Threading: the gate piggy-backs on the same /api/status polling
 * cadence live-wire.ts uses, so we don't add network load. We expose
 * a window-level handler so live-wire.ts can hand us each status
 * payload — no extra fetch loop.
 *
 * Same-origin to the runtime via CloudFront: /api/lp/config and
 * /api/lp/join-queue are served by the runtime's HTTP server. No
 * CORS needed.
 */

interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
}

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
  }) => void;
  prompt: () => void;
  cancel: () => void;
  renderButton: (el: HTMLElement, opts: Record<string, unknown>) => void;
}

interface GoogleNamespace {
  accounts?: { id?: GoogleAccountsId };
}

interface GoogleJwtPayload {
  email?: string;
  email_verified?: boolean;
  name?: string;
  picture?: string;
  sub?: string;
}

interface DriveGateConfig {
  google_client_id: string;
  slot_seconds: number;
  google_button_origin?: string;
}

interface LockSnapshot {
  holder_sid: string | null;
  holder_label: string | null;
  seconds_remaining: number | null;
  queue_length: number;
  queue: string[];
  you_are_holder?: boolean;
  your_position?: number | null;
}

interface SessionInfo {
  sid: string;
  is_admin: boolean;
}

interface RuntimeStatusLite {
  lock?: LockSnapshot;
  session?: SessionInfo;
}

type GateState = 'hidden' | 'signin' | 'queued' | 'driving' | 'thanks' | 'error';

const POLL_FALLBACK_MS = 1500;

let _config: DriveGateConfig | null = null;
let _state: GateState = 'hidden';
let _hasJoined = false;
let _gisLoaded = false;
let _pollTimer: number | null = null;
// Local 1Hz countdown so the "you're driving" timer ticks every second
// instead of only jumping on each status poll. Seeded from the last
// lock.seconds_remaining and decremented locally; the next poll
// re-syncs it so it never drifts far from the server's authority.
let _countdownDeadline = 0;   // epoch ms when the slot ends
let _countdownTimer: number | null = null;

/** Fire a GA4 event (gtag is loaded in index.html <head>). No-ops if
 *  the analytics script is blocked. Used to measure the drive funnel:
 *  CTA click → queue join → driving → completed. */
function track(event: string, params: Record<string, unknown> = {}): void {
  try {
    const g = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
    if (typeof g === 'function') g('event', event, params);
  } catch {
    /* analytics blocked — non-fatal */
  }
}

/** Show/hide + style the click-to-drive overlay and the driving badge
 *  over the live panel, based on lock state. Called from applyStatus.
 *  Modes: 'idle' (tap to drive), 'watching' (someone else drives),
 *  'driving' (badge only, overlay hidden so the holder can touch the
 *  panel), 'hidden' (no panel mounted). */
function setOverlay(mode: 'idle' | 'watching' | 'driving' | 'hidden', opts: {
  holder?: string;
} = {}): void {
  const overlay = document.getElementById('rigDriveOverlay');
  const badge = document.getElementById('rigDriveBadge');
  const line = document.getElementById('rigDriveLine');
  const sub = document.getElementById('rigDriveSub');
  if (overlay) {
    if (mode === 'driving' || mode === 'hidden') {
      overlay.hidden = true;
      overlay.classList.remove('watching');
    } else {
      overlay.hidden = false;
      overlay.classList.toggle('watching', mode === 'watching');
      if (mode === 'watching') {
        if (line) line.textContent = 'someone else is driving';
        if (sub) sub.textContent = opts.holder
          ? `${opts.holder} has the wheel`
          : 'tap a control to join the queue';
      } else {
        if (line) line.textContent = 'look around · touch anything to drive!';
        if (sub) sub.textContent = 'free · 75s at the wheel';
      }
    }
  }
  if (badge) badge.hidden = mode !== 'driving';
}

export function initDriveGate(): void {
  const cta = document.getElementById('lp-drive-cta') as HTMLButtonElement | null;
  const gate = document.getElementById('drive-gate');
  if (!cta || !gate) {
    // LP HTML hasn't been updated to include the gate; nothing to do.
    return;
  }

  cta.addEventListener('click', () => {
    track('drive_cta_click', { source: 'hero' });
    void openGate();
  });

  // The click-to-drive interaction is now caught INSIDE the panel
  // iframe (connection.ts attachPanelInteractionGuard) so visitors can
  // scroll/browse freely and only get the gate when they try to change
  // a control. The bottom hint banner (#rigDriveOverlay) is
  // non-interactive (pointer-events:none) — purely a visual cue.

  const manualForm = document.getElementById('drive-gate-manual') as HTMLFormElement | null;
  manualForm?.addEventListener('submit', (e) => {
    e.preventDefault();
    const fd = new FormData(manualForm);
    const name = String(fd.get('name') ?? '').trim();
    const email = String(fd.get('email') ?? '').trim();
    if (!name || !email) return;
    void joinQueue({ name, email, source: 'manual' });
  });

  document.getElementById('drive-gate-leave')?.addEventListener('click', () => {
    void releaseSlot();
  });
  document.getElementById('drive-gate-end')?.addEventListener('click', () => {
    void releaseSlot();
  });
  // GO — confirm "you're driving": close the modal and bring the live
  // panel into view so the visitor can immediately play. Their slot is
  // already held; this is just the hand-off gesture.
  document.getElementById('drive-gate-go')?.addEventListener('click', () => {
    track('drive_go_click', {});
    closeGate();
    document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  document.getElementById('drive-gate-again')?.addEventListener('click', () => {
    void openGate();
  });
  document.getElementById('drive-gate-retry')?.addEventListener('click', () => {
    setState('signin');
  });

  // Modal dismissal: × button, backdrop click, and Escape. None of
  // these touch queue/lock state — they just hide the modal; an
  // in-progress slot keeps running (the panel badge still shows it).
  document.getElementById('drive-gate-close')?.addEventListener('click', () => {
    closeGate();
  });
  gate.addEventListener('click', (e) => {
    // Click on the backdrop (the section itself), not the card.
    if (e.target === gate) closeGate();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !gate.hidden) closeGate();
  });

  // The gate piggy-backs on live-wire.ts's status polling. live-wire
  // exposes window.__lpStatusListeners so anyone can subscribe; if
  // it's not there yet (load order race), fall back to our own poll.
  attachStatusListener();

  // Expose openGate so the in-iframe interaction guard (connection.ts)
  // can pop the modal when a non-holder tries to touch a control.
  (window as unknown as { __lpOpenDriveGate?: () => void }).__lpOpenDriveGate = () => {
    track('drive_cta_click', { source: 'panel_control' });
    void openGate();
  };
}

async function openGate(): Promise<void> {
  const gate = document.getElementById('drive-gate');
  if (!gate) return;
  gate.hidden = false;
  // It's a centered modal now — show it in place, DON'T scroll the
  // page to it (that was the jarring jump). Lock body scroll while open.
  document.body.style.overflow = 'hidden';
  // Only reset to signin if we're not already mid-queue/driving.
  if (_state === 'hidden' || _state === 'signin' || _state === 'error' || _state === 'thanks') {
    setState('signin');
  }

  if (!_config) {
    await loadConfig();
  }
  if (_config?.google_client_id) {
    await ensureGisAndRender(_config.google_client_id);
  }
}

/** Dismiss the modal without affecting queue/lock state. If the
 *  visitor is queued or driving, the gate just hides — their slot
 *  continues; the panel badge/overlay still reflects it. */
function closeGate(): void {
  const gate = document.getElementById('drive-gate');
  if (gate) gate.hidden = true;
  document.body.style.overflow = '';
}

async function loadConfig(): Promise<void> {
  try {
    const res = await fetch('/api/lp/config', { credentials: 'include' });
    if (!res.ok) throw new Error(`/api/lp/config ${res.status}`);
    _config = (await res.json()) as DriveGateConfig;
  } catch (err) {
    console.warn('[drive-gate] config fetch failed', err);
    // Keep the manual fallback usable even without the Google button.
    _config = { google_client_id: '', slot_seconds: 300 };
  }
}

async function ensureGisAndRender(clientId: string): Promise<void> {
  if (!_gisLoaded) {
    await loadGisScript();
    _gisLoaded = true;
  }
  const google = (window as unknown as { google?: GoogleNamespace }).google;
  const id = google?.accounts?.id;
  if (!id) {
    console.warn('[drive-gate] GIS unavailable after script load');
    return;
  }
  id.initialize({
    client_id: clientId,
    callback: handleGoogleCredential,
    auto_select: false,
    cancel_on_tap_outside: false,
  });
  const buttonEl = document.getElementById('drive-gate-google');
  if (buttonEl) {
    buttonEl.innerHTML = '';
    id.renderButton(buttonEl, {
      type: 'standard',
      theme: 'filled_black',
      size: 'large',
      text: 'continue_with',
      shape: 'pill',
      logo_alignment: 'left',
    });
  }
}

function loadGisScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById('gis-script')) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.id = 'gis-script';
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('failed to load Google Identity Services'));
    document.head.appendChild(s);
  });
}

function handleGoogleCredential(response: GoogleCredentialResponse): void {
  const payload = decodeJwt(response.credential);
  if (!payload.email) {
    setState('error');
    showError('Google sign-in returned no email. Try the manual form below.');
    return;
  }
  void joinQueue({
    name: payload.name ?? '',
    email: payload.email,
    picture: payload.picture ?? '',
    credential: response.credential,
    source: 'google',
  });
}

function decodeJwt(credential: string): GoogleJwtPayload {
  try {
    const parts = credential.split('.');
    if (parts.length !== 3) return {};
    const payloadPart = parts[1];
    if (!payloadPart) return {};
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    // Re-pad — JWT base64url drops trailing =
    const padded = base64 + '==='.slice((base64.length + 3) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json) as GoogleJwtPayload;
  } catch {
    return {};
  }
}

interface JoinQueueArgs {
  name: string;
  email: string;
  picture?: string;
  credential?: string;
  source: 'google' | 'manual';
}

async function joinQueue(args: JoinQueueArgs): Promise<void> {
  try {
    const res = await fetch('/api/lp/join-queue', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(args),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const errMsg = (body as { error?: string }).error ?? `${res.status}`;
      showError(errMsg);
      setState('error');
      return;
    }
    _hasJoined = true;
    track('drive_queue_join', { method: args.source });
    const data = (await res.json()) as RuntimeStatusLite;
    applyStatus(data);
  } catch (err) {
    console.error('[drive-gate] join-queue failed', err);
    showError('Network glitch. Try again.');
    setState('error');
  }
}

async function releaseSlot(): Promise<void> {
  const wasDriving = _state === 'driving';
  try {
    await fetch('/api/lock/release', {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // best effort — even if the release call fails, the lock auto-
    // expires on the runtime side
  }
  if (wasDriving) {
    // Ended their own slot — thank them, then auto-dismiss.
    endSlot();
    return;
  }
  // Left the queue before driving — just dismiss.
  _hasJoined = false;
  setState('signin');
  closeGate();
}

function attachStatusListener(): void {
  // live-wire.ts may install a window.__lpStatusListeners array.
  // We push onto it so we don't duplicate polling. If it doesn't
  // exist yet, install our own slow poll loop.
  const w = window as unknown as {
    __lpStatusListeners?: Array<(s: RuntimeStatusLite) => void>;
  };
  if (!Array.isArray(w.__lpStatusListeners)) {
    w.__lpStatusListeners = [];
  }
  w.__lpStatusListeners.push(applyStatus);

  // Fallback: if no other consumer is polling within 2s, start our
  // own loop. live-wire.ts polls every ~1.5s for telemetry, so this
  // usually no-ops.
  setTimeout(() => {
    if (_pollTimer === null && _hasJoined) {
      startOwnPoll();
    }
  }, 2000);
}

function startOwnPoll(): void {
  if (_pollTimer !== null) return;
  const tick = async () => {
    if (!_hasJoined) {
      _pollTimer = null;
      return;
    }
    try {
      const res = await fetch('/api/status', { credentials: 'include' });
      if (res.ok) applyStatus((await res.json()) as RuntimeStatusLite);
    } catch {
      // ignore — try again next tick
    }
    _pollTimer = window.setTimeout(tick, POLL_FALLBACK_MS);
  };
  tick();
}

function applyStatus(s: RuntimeStatusLite): void {
  const lock = s.lock;
  // ── Always update the panel overlay/badge from lock state, even
  //    before the visitor joins — so the live panel shows "tap to
  //    drive" / "watching" / the driving badge correctly. Only acts
  //    when the panel iframe is mounted (overlay element not hidden by
  //    connection.ts). The session sid tells us if WE are the holder.
  const mySid = s.session?.sid;
  const iAmHolder = !!lock?.you_are_holder;
  // Share holder state so the in-iframe interaction guard (connection.ts)
  // knows whether to intercept control clicks. When you hold the slot,
  // the panel is yours — no interception.
  (window as unknown as { __lpIsHolder?: boolean }).__lpIsHolder = iAmHolder;
  if (lock) {
    if (iAmHolder) {
      setOverlay('driving');
    } else if (lock.holder_sid && lock.holder_sid !== mySid) {
      const holder = lock.holder_label ? maskEmail(lock.holder_label) : undefined;
      setOverlay('watching', holder ? { holder } : {});
    } else {
      setOverlay('idle');
    }
  }

  if (!_hasJoined || _state === 'hidden') return;
  if (!lock) return;

  if (lock.you_are_holder) {
    if (_state !== 'driving') track('drive_start', {});
    setState('driving');
    // Smooth countdown: the local 1Hz ticker owns the display between
    // polls. We only (re)seed the deadline from the server when it
    // drifts more than 2s from our local view — otherwise re-seeding
    // every poll fights the smooth tick and makes it jump. First lock
    // (deadline 0) always seeds.
    const remain = Math.max(0, Math.round(lock.seconds_remaining ?? 0));
    const serverDeadline = Date.now() + remain * 1000;
    if (_countdownDeadline === 0
        || Math.abs(serverDeadline - _countdownDeadline) > 2000) {
      _countdownDeadline = serverDeadline;
    }
    startCountdown();
    renderCountdown();
    return;
  }

  if (typeof lock.your_position === 'number' && lock.your_position >= 0) {
    stopCountdown();
    setState('queued');
    const posEl = document.getElementById('drive-gate-pos');
    if (posEl) posEl.textContent = String(lock.your_position + 1);

    const slotSeconds = _config?.slot_seconds ?? 300;
    const aheadOfMe = lock.your_position; // count of waiters ahead
    const currentRemain = Math.max(0, Math.round(lock.seconds_remaining ?? 0));
    const etaSeconds = currentRemain + aheadOfMe * slotSeconds;
    const etaEl = document.getElementById('drive-gate-eta');
    if (etaEl) etaEl.textContent = `~${formatEta(etaSeconds)} wait`;

    const holderEl = document.getElementById('drive-gate-holder');
    if (holderEl) {
      holderEl.textContent = lock.holder_label
        ? maskEmail(lock.holder_label)
        : 'someone';
    }
    return;
  }

  // We were in queue or driving but now we're nowhere — slot
  // released voluntarily or the lock cleared.
  if (_state === 'driving') {
    // The drive slot just ended (timer hit zero server-side or the
    // lock was reclaimed). Thank the player rather than silently
    // bouncing them back to the sign-in form.
    endSlot();
    return;
  }
  if (_state === 'queued') {
    _hasJoined = false;
    setState('signin');
    closeGate();
  }
}

/** Render the driving countdown from the local deadline — both the
 *  gate card timer and the small badge over the live panel. */
function renderCountdown(): void {
  const remain = Math.max(0, Math.round((_countdownDeadline - Date.now()) / 1000));
  const m = Math.floor(remain / 60);
  const sec = remain % 60;
  const text = `${m}:${sec.toString().padStart(2, '0')}`;
  const el = document.getElementById('drive-gate-countdown');
  if (el) el.textContent = text;
  const badge = document.getElementById('rigDriveCountdown');
  if (badge) badge.textContent = text;
}

/** 1Hz local ticker so the countdown updates every second between
 *  status polls. Re-seeded by applyStatus each poll. When it reaches
 *  zero locally we show the thanks state immediately (the next poll
 *  confirms the server released the lock). */
function startCountdown(): void {
  if (_countdownTimer !== null) return;
  _countdownTimer = window.setInterval(() => {
    if (_state !== 'driving') {
      stopCountdown();
      return;
    }
    renderCountdown();
    if (_countdownDeadline - Date.now() <= 0) {
      endSlot();
    }
  }, 1000);
}

function stopCountdown(): void {
  if (_countdownTimer !== null) {
    clearInterval(_countdownTimer);
    _countdownTimer = null;
  }
}

/** End the drive slot: thank the player, then auto-dismiss back to a
 *  fresh sign-in after a few seconds so the next person can join. */
function endSlot(): void {
  stopCountdown();
  track('drive_complete', {});
  _hasJoined = false;
  _countdownDeadline = 0;   // so the next drive re-seeds cleanly
  setState('thanks');
  window.setTimeout(() => {
    if (_state !== 'thanks') return; // they already re-joined
    setState('signin');
    closeGate();
  }, 6000);
}

function setState(next: GateState): void {
  _state = next;
  const gate = document.getElementById('drive-gate');
  if (gate) gate.dataset.driveState = next;
  document.querySelectorAll<HTMLDivElement>('.drive-gate-state').forEach((el) => {
    el.hidden = el.dataset.state !== next;
  });
}

function showError(msg: string): void {
  const el = document.getElementById('drive-gate-error');
  if (el) el.textContent = msg;
}

function formatEta(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

function maskEmail(label: string): string {
  // Show "ja…@example.com" for privacy. We don't want the queue
  // surface to leak full emails of other visitors.
  const at = label.indexOf('@');
  if (at <= 0) return label;
  const local = label.slice(0, at);
  const domain = label.slice(at);
  if (local.length <= 2) return `${local[0] ?? ''}…${domain}`;
  return `${local.slice(0, 2)}…${domain}`;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDriveGate);
} else {
  initDriveGate();
}
